/**
 * AccessMatrixPage — Vincent #198 (2026-05-28)
 *
 * Matrice Rôles × Items menu : permet aux Admin/VP/CEO/Président d'une
 * entreprise de cacher des items du menu de gauche pour certains rôles.
 *
 * Logique :
 * - On charge la matrice côté serveur (`GET /api/org/access-matrix`)
 * - On charge la structure du menu via `buildMenuConfig` (même source que
 *   le DashboardLayout) → tous les items dispos
 * - L'utilisateur coche/décoche : "cocher" = visible, "décocher" = caché
 * - Sauvegarde via `PUT /api/org/access-matrix`
 * - Les rôles `ceo` et `president` sont en lecture seule (anti-lockout).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import {
  ShieldCheck, FloppyDisk, ArrowCounterClockwise, Check, X as XIcon,
  Warning, Info, CaretRight, CaretDown,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { buildMenuConfig } from "../config/menuConfig";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE as API } from "../lib/apiBase";
import { getCanonicalLabel, roleLabelsFor } from "../lib/canonicalRoles";

// Vincent #216 (2026-02-06) — Les étiquettes courtes ("Accès lecture limité",
// "Membre standard", etc.) avaient été INVENTÉES et ne correspondaient à rien
// dans la Gestion des rôles. Retirées : on affiche uniquement l'identifiant
// technique canonique du rôle (founder, admin, concepteur, employee_advanced,
// employee_trained, employee_new, viewer) — source unique de vérité, alignée
// sur les rôles définis dans `core/permissions.py` et `lib/canonicalRoles.js`.

// Minimal icon stub — AccessMatrixPage n'affiche pas les icônes du menu,
// elle a juste besoin de la STRUCTURE (paths + labels). On passe des stubs
// pour satisfaire buildMenuConfig sans tirer toute la lib Phosphor.
const ICON_STUB = () => null;
const ICONS_PROXY = new Proxy({}, { get: () => ICON_STUB });

// Vincent 2026-06-01 — Libellés des rôles tirés de la source unique
// (lib/canonicalRoles.js + roleLabelsFor(user, lang) personnalisés par org).

export default function AccessMatrixPage() {
  const { user } = useAuth();
  const { lang, t } = useLang();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editableRoles, setEditableRoles] = useState([]);
  const [protectedRoles, setProtectedRoles] = useState([]);
  const [overrides, setOverrides] = useState({}); // {role: Set<path>}  (HIDDEN paths)
  const [initialOverrides, setInitialOverrides] = useState({});
  // Vincent #325 (Eric 2026-06-17 SPEC STRICK) — Onglet "Créer/éditer le contenu"
  const [editOverrides, setEditOverrides] = useState({}); // {role: Set<path>}  (NOT-EDITABLE paths)
  const [initialEditOverrides, setInitialEditOverrides] = useState({});
  const [copyFromView, setCopyFromView] = useState(true);
  const [initialCopyFromView, setInitialCopyFromView] = useState(true);
  const [neverEditablePaths, setNeverEditablePaths] = useState([]);
  const [extraEditPaths, setExtraEditPaths] = useState([]);
  const [activeTab, setActiveTab] = useState("view"); // "view" | "edit"
  const [expandedPillars, setExpandedPillars] = useState(new Set());

  // Build the menu structure for the current user (gives us all visible paths).
  // We use ICONS_PROXY because we don't render icons here, just structure.
  const menu = useMemo(() => buildMenuConfig(ICONS_PROXY, t, lang), [t, lang]);

  useEffect(() => {
    setExpandedPillars(new Set(menu.map(p => p.id)));
  }, [menu]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/org/access-matrix`, { withCredentials: true });
      setEditableRoles(data.editable_roles || []);
      setProtectedRoles(data.protected_roles || []);
      const o = {};
      Object.entries(data.role_access_overrides || {}).forEach(([role, paths]) => {
        o[role] = new Set(paths);
      });
      setOverrides(o);
      setInitialOverrides(JSON.parse(JSON.stringify(Object.fromEntries(
        Object.entries(o).map(([k, v]) => [k, Array.from(v).sort()])
      ))));
      // Vincent #325 — Onglet "Créer/éditer le contenu"
      const eo = {};
      Object.entries(data.role_edit_overrides || {}).forEach(([role, paths]) => {
        eo[role] = new Set(paths);
      });
      setEditOverrides(eo);
      setInitialEditOverrides(JSON.parse(JSON.stringify(Object.fromEntries(
        Object.entries(eo).map(([k, v]) => [k, Array.from(v).sort()])
      ))));
      const copyDefault = data.edit_copy_from_view !== false;
      setCopyFromView(copyDefault);
      setInitialCopyFromView(copyDefault);
      setNeverEditablePaths(data.never_editable_paths || []);
      setExtraEditPaths(data.extra_edit_paths || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const isHidden = (role, path) => overrides[role]?.has(path);

  const toggle = (role, path) => {
    if (protectedRoles.includes(role)) return;
    setOverrides(prev => {
      const next = { ...prev };
      const set = new Set(next[role] || []);
      if (set.has(path)) set.delete(path);
      else set.add(path);
      next[role] = set;
      return next;
    });
  };

  // Vincent #325 (Eric 2026-06-17 SPEC STRICK) — Helpers onglet "Créer/éditer le contenu"
  const isNotEditable = (role, path) => {
    if (neverEditablePaths.includes(path)) return true;
    if (isHidden(role, path)) return true; // grisé car non-visible
    if (copyFromView) {
      // Recopie : édition autorisée par défaut si visible
      return false;
    }
    return editOverrides[role]?.has(path);
  };
  const isEditCellLocked = (role, path) => {
    return neverEditablePaths.includes(path) || isHidden(role, path);
  };
  const toggleEdit = (role, path) => {
    if (protectedRoles.includes(role)) return;
    if (isEditCellLocked(role, path)) return;
    setEditOverrides(prev => {
      const next = { ...prev };
      const set = new Set(next[role] || []);
      if (set.has(path)) set.delete(path);
      else set.add(path);
      next[role] = set;
      return next;
    });
  };

  const togglePillar = (pillarId) => {
    setExpandedPillars(prev => {
      const next = new Set(prev);
      if (next.has(pillarId)) next.delete(pillarId);
      else next.add(pillarId);
      return next;
    });
  };

  const hasChanges = useMemo(() => {
    const cur = JSON.stringify(Object.fromEntries(
      Object.entries(overrides).map(([k, v]) => [k, Array.from(v).sort()])
    ));
    const init = JSON.stringify(initialOverrides);
    const curEdit = JSON.stringify(Object.fromEntries(
      Object.entries(editOverrides).map(([k, v]) => [k, Array.from(v).sort()])
    ));
    const initEdit = JSON.stringify(initialEditOverrides);
    return cur !== init || curEdit !== initEdit || copyFromView !== initialCopyFromView;
  }, [overrides, initialOverrides, editOverrides, initialEditOverrides, copyFromView, initialCopyFromView]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        role_access_overrides: Object.fromEntries(
          Object.entries(overrides)
            .filter(([role]) => editableRoles.includes(role))
            .map(([role, set]) => [role, Array.from(set)])
        ),
        role_edit_overrides: Object.fromEntries(
          Object.entries(editOverrides)
            .filter(([role]) => editableRoles.includes(role))
            .map(([role, set]) => [role, Array.from(set)])
        ),
        edit_copy_from_view: copyFromView,
      };
      await axios.put(`${API}/api/org/access-matrix`, payload, { withCredentials: true });
      toast.success(lang === "en" ? "Access matrix saved" : "Matrice d'accès sauvegardée");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (hasChanges) {
      const ok = await confirmDialog(lang === "en" ? "Discard changes?" : "Annuler les modifications ?");
      if (!ok) return;
    }
    await load();
  };

  const setAllForRole = (role, hidden) => {
    if (protectedRoles.includes(role)) return;
    setOverrides(prev => {
      const next = { ...prev };
      const set = new Set();
      if (hidden) {
        menu.forEach(p => p.sections.forEach(s => s.items.forEach(i => set.add(i.path))));
      }
      next[role] = set;
      return next;
    });
  };

  const allRoles = [...editableRoles, ...protectedRoles];

  if (loading) {
    return <div className="p-12 text-center text-zinc-500">{lang === "en" ? "Loading…" : "Chargement…"}</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="access-matrix-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center border border-blue-500/20">
            <ShieldCheck size={24} weight="duotone" className="text-[#002FA7]" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold font-['Outfit']">
              {lang === "en" ? "Access Matrix" : "Sélection des accès"}
            </h1>
            <p className="text-xs text-zinc-500">
              {lang === "en"
                ? "Choose which menu items each role can see in this organization."
                : "Choisissez quels items du menu chaque rôle peut voir dans cette entreprise."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={reset} disabled={!hasChanges || saving} className="gap-1.5" data-testid="reset-btn">
            <ArrowCounterClockwise size={14} /> {lang === "en" ? "Reset" : "Annuler"}
          </Button>
          <Button onClick={save} disabled={!hasChanges || saving} className="gap-1.5 bg-[#002FA7] hover:bg-[#002280]" data-testid="save-btn">
            <FloppyDisk size={14} weight={hasChanges ? "fill" : "regular"} />
            {saving ? "…" : (lang === "en" ? "Save changes" : "Enregistrer")}
          </Button>
        </div>
      </div>

      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 text-xs text-amber-800" data-testid="lockout-warning">
        <Warning size={18} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
        <div>
          <strong>
            {lang === "en"
              ? `${protectedRoles.map(r => getCanonicalLabel(r, roleLabelsFor(user, lang), false, lang)).join(" and ")} are protected`
              : `Les rôles de "${protectedRoles.map(r => getCanonicalLabel(r, roleLabelsFor(user, lang), false, lang)).join('" et "')}" sont protégés`}
          </strong>
          {" — "}
          {lang === "en"
            ? "to prevent any accidental lockout. Their checkboxes are read-only."
            : "pour éviter tout lock-out accidentel. Leurs cases sont en lecture seule."}
        </div>
      </div>

      {/* Vincent #325 (Eric 2026-06-17 SPEC STRICK) — 2 onglets : Voir les menus / Créer-éditer le contenu */}
      <div className="mb-4 flex gap-1 p-1 bg-zinc-100 rounded-lg w-fit" data-testid="access-tabs">
        <button
          onClick={() => setActiveTab("view")}
          className={`px-4 py-2 text-sm rounded-md transition-colors font-medium ${
            activeTab === "view" ? "bg-white text-[#002FA7] shadow-sm" : "text-zinc-600 hover:text-zinc-900"
          }`}
          data-testid="tab-view-menus"
        >
          {lang === "fr" ? "Voir les menus" : "View menus"}
        </button>
        <button
          onClick={() => setActiveTab("edit")}
          className={`px-4 py-2 text-sm rounded-md transition-colors font-medium ${
            activeTab === "edit" ? "bg-white text-[#002FA7] shadow-sm" : "text-zinc-600 hover:text-zinc-900"
          }`}
          data-testid="tab-edit-content"
        >
          {lang === "fr" ? "Créer/éditer le contenu" : "Create/edit content"}
        </button>
      </div>

      {activeTab === "edit" && (
        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-xs" data-testid="copy-from-view-banner">
          <input
            type="checkbox"
            id="copy-from-view"
            checked={copyFromView}
            onChange={(e) => setCopyFromView(e.target.checked)}
            className="w-4 h-4 cursor-pointer"
            data-testid="copy-from-view-checkbox"
          />
          <label htmlFor="copy-from-view" className="cursor-pointer text-blue-900 font-medium">
            {lang === "fr"
              ? 'Recopier les réglages de la page "Voir les menus"'
              : 'Copy settings from "View menus" tab'}
          </label>
        </div>
      )}

      {/* Matrix table — Vincent #371 (2026-06-22) : sticky header verticale.
          Le conteneur a maintenant une hauteur max + scroll Y/X pour que le
          <thead sticky top-0> s'accroche réellement en haut quand l'utilisateur
          défile le tableau vers le bas. */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-auto max-h-[calc(100vh-280px)]" data-testid="matrix-container">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0 z-20">
            <tr>
              <th className="text-left px-3 py-3 font-semibold text-zinc-700 sticky left-0 bg-zinc-50 min-w-[300px]">
                {lang === "en" ? "Menu item" : "Item du menu"}
              </th>
              {allRoles.map(role => (
                <th key={role} className={`text-center px-2 py-3 font-semibold whitespace-nowrap ${protectedRoles.includes(role) ? "text-zinc-400 bg-zinc-100" : "text-zinc-700"}`} data-testid={`role-header-${role}`}>
                  <div>{getCanonicalLabel(role, roleLabelsFor(user, lang), false, lang)}</div>
                  {/* Vincent #216 (2026-02-06) — Sous le libellé canonique, on
                      affiche UNIQUEMENT l'identifiant technique du rôle (pas
                      d'étiquette inventée). C'est la valeur exacte stockée en
                      DB et utilisée par les permissions backend. */}
                  <div className="text-[10px] font-mono font-normal text-zinc-400 mt-0.5 max-w-[120px] mx-auto leading-tight" data-testid={`role-tagline-${role}`}>
                    {role}
                  </div>
                  {!protectedRoles.includes(role) && (
                    <div className="flex justify-center gap-1 mt-1">
                      <button
                        onClick={() => setAllForRole(role, false)}
                        className="text-[9px] px-1 py-0.5 rounded bg-teal-100 text-teal-700 hover:bg-teal-200"
                        title={lang === "en" ? "Show all" : "Tout afficher"}
                        data-testid={`show-all-${role}`}
                      >
                        {lang === "en" ? "All ✓" : "Tout ✓"}
                      </button>
                      <button
                        onClick={() => setAllForRole(role, true)}
                        className="text-[9px] px-1 py-0.5 rounded bg-rose-100 text-rose-700 hover:bg-rose-200"
                        title={lang === "en" ? "Hide all" : "Tout cacher"}
                        data-testid={`hide-all-${role}`}
                      >
                        {lang === "en" ? "None ✗" : "Aucun ✗"}
                      </button>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {menu.map(pillar => (
              <PillarRows
                key={pillar.id}
                pillar={pillar}
                allRoles={allRoles}
                protectedRoles={protectedRoles}
                isHidden={activeTab === "view" ? isHidden : isNotEditable}
                toggle={activeTab === "view" ? toggle : toggleEdit}
                isLocked={activeTab === "edit" ? isEditCellLocked : null}
                expanded={expandedPillars.has(pillar.id)}
                onTogglePillar={() => togglePillar(pillar.id)}
              />
            ))}
            {/* Vincent #337 — Permissions transverses (boutons spéciaux) dans l'onglet édition */}
            {activeTab === "edit" && extraEditPaths.length > 0 && (
              <>
                <tr className="bg-zinc-100/60 border-b border-zinc-200">
                  <td colSpan={allRoles.length + 1} className="sticky left-0 px-2 py-1.5">
                    <span className="font-semibold text-zinc-800">
                      {lang === "fr" ? "Permissions transverses" : "Cross-cutting permissions"}
                    </span>
                  </td>
                </tr>
                {extraEditPaths.map((path) => (
                  <tr key={path} className="border-b border-zinc-100">
                    <td className="px-3 py-1.5 sticky left-0 bg-white">
                      <div className="font-medium text-zinc-800">
                        {path === "/dashboard/data-validation/download-document"
                          ? (lang === "fr" ? "Télécharger le document (Validation des données)" : "Download document (Data validation)")
                          : path}
                      </div>
                      <div className="text-[10px] text-zinc-400 font-mono">{path}</div>
                    </td>
                    {allRoles.map((role) => {
                      const checked = !isNotEditable(role, path);
                      const isProtected = protectedRoles.includes(role);
                      const locked = isEditCellLocked(role, path);
                      return (
                        <td key={role} className={`text-center px-1 py-1.5 ${isProtected ? "bg-zinc-50/60" : ""}`}>
                          <button
                            type="button"
                            onClick={() => toggleEdit(role, path)}
                            disabled={isProtected || locked}
                            className={`w-5 h-5 rounded inline-flex items-center justify-center border ${
                              checked
                                ? "bg-teal-500 border-teal-600 text-white"
                                : "bg-rose-100 border-rose-300 text-rose-600"
                            } ${(isProtected || locked) ? "opacity-40 cursor-not-allowed" : "hover:scale-110 transition-transform"}`}
                            data-testid={`cell-edit-${role}-${path.replace(/[^a-z0-9]/gi, '-')}`}
                          >
                            {checked ? <Check size={12} weight="bold" /> : <XIcon size={12} weight="bold" />}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2 text-xs text-blue-800">
        <Info size={16} className="shrink-0 mt-0.5" />
        <div>
          {lang === "en"
            ? "A checked box means the item is VISIBLE. Uncheck to hide it for that role. Changes apply on the next page load for affected users."
            : "Une case cochée = item VISIBLE. Décocher pour le cacher à ce rôle. Les changements s'appliquent au prochain chargement de page pour les utilisateurs concernés."}
        </div>
      </div>
    </div>
  );
}


function PillarRows({ pillar, allRoles, protectedRoles, isHidden, toggle, isLocked, expanded, onTogglePillar }) {
  return (
    <>
      <tr className="bg-zinc-100/60 border-b border-zinc-200">
        <td colSpan={allRoles.length + 1} className="sticky left-0 px-2 py-1.5">
          <button onClick={onTogglePillar} className="flex items-center gap-1.5 font-semibold text-zinc-800 hover:text-[#002FA7]" data-testid={`pillar-toggle-${pillar.id}`}>
            {expanded ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
            {pillar.title}
          </button>
        </td>
      </tr>
      {expanded && pillar.sections.map(section => (
        <SectionRows key={section.id} pillar={pillar} section={section} allRoles={allRoles} protectedRoles={protectedRoles} isHidden={isHidden} toggle={toggle} isLocked={isLocked} />
      ))}
    </>
  );
}


function SectionRows({ pillar, section, allRoles, protectedRoles, isHidden, toggle, isLocked }) {
  return (
    <>
      <tr className="bg-zinc-50">
        <td colSpan={allRoles.length + 1} className="sticky left-0 px-3 py-1 text-[10px] font-bold uppercase text-zinc-500 tracking-wider">
          {pillar.title} › {section.title}
        </td>
      </tr>
      {section.items.map(item => (
        <tr key={item.path} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`row-${item.path.replace(/[^a-z0-9]/gi, '-')}`}>
          <td className="px-3 py-1.5 sticky left-0 bg-white">
            <div className="font-medium text-zinc-800">{item.label}</div>
            <div className="text-[10px] text-zinc-400 font-mono">{item.path}</div>
          </td>
          {allRoles.map(role => {
            const checked = !isHidden(role, item.path);
            const isProtected = protectedRoles.includes(role);
            const locked = isLocked ? isLocked(role, item.path) : false;
            return (
              <td key={role} className={`text-center px-1 py-1.5 ${isProtected ? "bg-zinc-50/60" : ""}`}>
                <button
                  type="button"
                  onClick={() => toggle(role, item.path)}
                  disabled={isProtected || locked}
                  className={`w-5 h-5 rounded inline-flex items-center justify-center border ${
                    checked
                      ? "bg-teal-500 border-teal-600 text-white"
                      : "bg-rose-100 border-rose-300 text-rose-600"
                  } ${(isProtected || locked) ? "opacity-40 cursor-not-allowed" : "hover:scale-110 transition-transform"}`}
                  data-testid={`cell-${role}-${item.path.replace(/[^a-z0-9]/gi, '-')}`}
                  title={locked ? (checked ? "Verrouillé (Voir les menus)" : "Verrouillé (non visible)") : (checked ? "Visible" : "Caché")}
                >
                  {checked ? <Check size={12} weight="bold" /> : <XIcon size={12} weight="bold" />}
                </button>
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
